import api from '../../services/api';
import { useState, useEffect } from "react";
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import PageContainer from '../../common/components/PageContainer';
import useAlerta from '../../common/components/useAlerta';
import AppSnackbar from '../../common/components/AppSnackbar';
import usePermissoes from '../../common/hooks/usePermissoes';
import Permissoes from '../../common/permissoes';

function Feed() {
  const { hasPermission } = usePermissoes();
  const podePublicar = hasPermission(Permissoes.FEED);
  const [postagens, setPostagens] = useState([]);
  const [texto, setTexto] = useState('');
  const [foto, setFoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [publicando, setPublicando] = useState(false);
  const [nomeUsuario, setNomeUsuario] = useState('');
  const { alerta, mostrarAlerta, fecharAlerta } = useAlerta();

  const funcionarioId = localStorage.getItem('id');
  const fotoUsuario = localStorage.getItem('foto');

  const formatarDataBR = (dataISO) => {
    if (!dataISO) return '';

    const data = new Date(dataISO);
    return data.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatarTempoRelativo = (dataISO) => {
    if (!dataISO) return '';

    const diffMs = new Date() - new Date(dataISO);
    const diffMin = Math.floor(diffMs / 60000);

    if (diffMin < 1) return 'agora mesmo';
    if (diffMin < 60) return `há ${diffMin} min`;

    const diffHoras = Math.floor(diffMin / 60);
    if (diffHoras < 24) return `há ${diffHoras}h`;

    const diffDias = Math.floor(diffHoras / 24);
    if (diffDias < 7) return `há ${diffDias}d`;

    return formatarDataBR(dataISO);
  };

  const fetchPostagens = async () => {
    try {
      const response = await api.get('/postagens');
      const lista = response.data._embedded?.postagemModelList || [];
      setPostagens(lista);
    } catch (error) {
      console.error('Erro ao buscar postagens:', error);
      mostrarAlerta('Erro ao carregar o feed!', 'error');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    fetchPostagens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function fetchFuncionario() {
      if (!funcionarioId) return;
      try {
        const response = await api.get(`/funcionario/${funcionarioId}`);
        const primeiroNome = response.data?.nome?.split(' ')[0];
        if (primeiroNome) setNomeUsuario(primeiroNome);
      } catch (error) {
        console.error('Erro ao buscar funcionário:', error);
      }
    }
    fetchFuncionario();
  }, [funcionarioId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!texto && !foto) {
      mostrarAlerta('Escreva algo ou adicione uma imagem', 'warning');
      return;
    }

    setPublicando(true);
    try {
      const formData = new FormData();
      formData.append('texto', texto);
      formData.append('funcionarioId', funcionarioId);

      if (foto) {
        formData.append('foto', foto);
      }

      await api.post('/postagens', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setTexto('');
      setFoto(null);
      setPreview(null);

      fetchPostagens();

    } catch (error) {
      console.error('Erro ao criar postagem:', error);
      mostrarAlerta('Erro ao publicar. Tente novamente.', 'error');
    } finally {
      setPublicando(false);
    }
  };

  const handleFotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFoto(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoverFoto = () => {
    setFoto(null);
    setPreview(null);
  };

  const getImageSrc = (base64) => {
    if (!base64) return null;
    return `data:image/jpeg;base64,${base64}`;
  };

  return (
    <PageContainer sx={{ maxWidth: 640 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600 }}>
          {nomeUsuario ? `Bem-vindo, ${nomeUsuario}!` : 'Feed'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Fique por dentro do que está acontecendo na JAP
        </Typography>
      </Box>

      {podePublicar && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack component="form" onSubmit={handleSubmit} direction="row" spacing={1.5} alignItems="flex-start">
              <Avatar
                src={fotoUsuario ? `data:image/jpeg;base64,${fotoUsuario}` : undefined}
                sx={{ bgcolor: 'primary.main' }}
              >
                {!fotoUsuario && (nomeUsuario ? nomeUsuario.charAt(0) : 'U')}
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  placeholder="No que você está pensando?"
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  multiline
                  minRows={2}
                  maxRows={10}
                  fullWidth
                  sx={{ mb: preview ? 1.5 : 2 }}
                />

                {preview && (
                  <Box sx={{ position: 'relative', mb: 2 }}>
                    <Box
                      component="img"
                      src={preview}
                      alt="Preview"
                      sx={{ width: '100%', maxHeight: 300, objectFit: 'cover', borderRadius: 1.5 }}
                    />
                    <Tooltip title="Remover foto">
                      <IconButton
                        size="small"
                        onClick={handleRemoverFoto}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'rgba(10, 26, 43, 0.7)',
                          color: '#fff',
                          '&:hover': { bgcolor: 'rgba(10, 26, 43, 0.9)' },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Button component="label" startIcon={<ImageOutlinedIcon />} color="primary">
                    Foto
                    <input type="file" accept="image/*" hidden onChange={handleFotoChange} />
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    disabled={(!texto && !foto) || publicando}
                  >
                    {publicando ? 'Publicando...' : 'Publicar'}
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {carregando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress color="primary" size={32} />
        </Box>
      ) : (
        <Stack spacing={2}>
          {postagens.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <ForumOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">
                Nenhuma postagem ainda. Seja o primeiro a compartilhar algo!
              </Typography>
            </Box>
          )}

          {postagens.map((post) => (
            <Card
              key={post.id}
              sx={{
                transition: 'box-shadow 0.2s ease',
                '&:hover': { boxShadow: '0 4px 12px rgba(20, 44, 67, 0.15)' },
              }}
            >
              <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                  <Avatar
                    src={post.funcionario?.foto ? `data:image/jpeg;base64,${post.funcionario.foto}` : undefined}
                    sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}
                  >
                    {post.funcionario?.nome?.charAt(0) || '?'}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {post.funcionario?.nome}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" title={formatarDataBR(post.dataPostagem)}>
                      {formatarTempoRelativo(post.dataPostagem)}
                    </Typography>
                  </Box>
                </Stack>

                {post.texto && (
                  <Typography sx={{ whiteSpace: 'pre-wrap', mb: post.foto ? 1.5 : 0 }}>
                    {post.texto}
                  </Typography>
                )}

                {post.foto && (
                  <Box
                    component="img"
                    src={getImageSrc(post.foto)}
                    alt="Postagem"
                    sx={{ width: '100%', maxHeight: 400, objectFit: 'contain', borderRadius: 1.5 }}
                  />
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <AppSnackbar alerta={alerta} onClose={fecharAlerta} />
    </PageContainer>
  );
}

export default Feed;
